import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckSquare, Upload, Image, FileText, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../services/api';

const AccountabilityModule = () => {
  const [evidence, setEvidence] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [evidenceRes, alertsRes] = await Promise.all([
        api.evidence.getAll(),
        api.alerts.getAll({ limit: 50 })
      ]);
      // Handle both array and {data: []} response formats
      const evidenceData = evidenceRes.data?.data || evidenceRes.data || [];
      const alertsData = alertsRes.data?.data || alertsRes.data || [];
      setEvidence(Array.isArray(evidenceData) ? evidenceData : []);
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setEvidence([]);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadEvidence = () => {
    if (!selectedAlert || !uploadDescription) {
      alert('Please select an alert and provide a description');
      return;
    }
    alert('In production, this would upload a file and create evidence record');
  };

  const getAlertById = (alertId) => {
    return alerts.find(a => a.id === alertId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading accountability data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accountability-module">
      {/* Upload Evidence */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload size={20} />
            Upload Evidence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Alert/Issue</label>
                <select
                  value={selectedAlert}
                  onChange={(e) => setSelectedAlert(e.target.value)}
                  className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2"
                >
                  <option value="">Select an alert...</option>
                  {alerts.map((alert) => (
                    <option key={alert.id} value={alert.id}>
                      {alert.id} - {alert.type} ({alert.severity})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Description</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Describe the evidence being uploaded..."
                  className="w-full bg-slate-900 border-cyan-500/30 hover:border-cyan-500/50 transition-colors text-white rounded px-3 py-2 min-h-[100px]"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Upload File</label>
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                  <Upload size={32} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-400">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500 mt-1">Images, Videos, PDFs (Max 10MB)</p>
                </div>
              </div>

              <Button onClick={handleUploadEvidence} className="w-full">
                Submit Evidence
              </Button>
            </div>

            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="text-sm font-semibold mb-4">Evidence Guidelines</h3>
              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Take clear photos showing the issue and resolution</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Include timestamps and location information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Document before and after states</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Add detailed descriptions of actions taken</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckSquare size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Upload supporting documents (reports, invoices)</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evidence Gallery */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle>Evidence Gallery ({evidence.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {evidence.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No evidence uploaded yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {evidence.map((item) => {
                const alert = getAlertById(item.alert_id);
                return (
                  <div key={item.id} className="bg-slate-800 rounded-lg overflow-hidden hover:ring-2 ring-blue-500 transition-all cursor-pointer">
                    <div className="aspect-video bg-slate-700 flex items-center justify-center">
                      <Image size={48} className="text-gray-500" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {item.file_url.endsWith('.pdf') ? (
                          <FileText size={16} className="text-red-400" />
                        ) : (
                          <Image size={16} className="text-blue-400" />
                        )}
                        <span className="text-xs text-gray-400">Evidence</span>
                      </div>
                      {alert && (
                        <p className="text-sm font-semibold mb-1">{alert.type}</p>
                      )}
                      <p className="text-xs text-gray-400 mb-2">{item.description || 'No description'}</p>
                      {item.latitude && item.longitude && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <MapPin size={12} />
                          {item.latitude.toFixed(4)}, {item.longitude.toFixed(4)}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(item.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountabilityModule;
